const USER_STORAGE_KEY = 'softhread-user';

/**
 * Is there a usable session right now?
 *
 * Read straight from storage rather than from the store, because this is
 * called from `ngxsOnInit` — before any state has been restored.
 *
 * Why it exists: six feature states each fetch data in `ngxsOnInit`, and those
 * hooks run once when the app boots, on EVERY route. On the public break-glass
 * pages — the emergency card a paramedic scans — that meant a signed-out
 * browser firing a burst of authenticated requests that can only ever fail,
 * competing with the one request that actually matters. The break-glass path
 * has to be the most robust thing here, not the most crowded.
 */
export function hasLiveSession(): boolean {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const user = JSON.parse(raw) as { token?: string; role?: string } | null;
    // A signed-in user, whether or not their ACCESS token is still fresh. It
    // used to also require the token to be unexpired, which turned a stale
    // token — the normal state after an hour away — into "no session": the
    // feature states then skipped their first fetch and never got another,
    // while every other request on the page refreshed the token through the
    // interceptor and succeeded. Expiry is the interceptor's job; this only
    // has to tell a signed-in browser from an anonymous break-glass scan.
    return !!user && !!user.token && !!user.role;
  } catch {
    // Unreadable storage is not a session.
    return false;
  }
}
